import { createSelector } from '@reduxjs/toolkit';

import { PaymentState } from '~/renderer/store/receipt/types';

import { getIsConnected } from '../connection/selectors';

import type { AppState } from '../types';
import type { Sale, Tip, TipOption } from '~/renderer/store/receipt/types';

export const getSale = (state: AppState): Sale | null => state.receipt?.sale;
export const getIsSaleVisible = (state: AppState): boolean => state.receipt.isSaleVisible;
export const getIsPaymentStaged = (state: AppState): boolean =>
  state.receipt.paymentState === PaymentState.PAYMENT_STARTED;
export const getIsCustomerInfoVisible = (state: AppState) =>
  state.receipt.isCustomerInfoViewVisible;

const getIsSaleCompleted = createSelector([getSale], sale => {
  return sale?.completed ?? true;
});

export const getIsActive = createSelector(
  [getIsConnected, getIsSaleVisible, getIsSaleCompleted],
  (isConnected, isSaleVisible, isSaleCompleted) => isConnected && isSaleVisible && !isSaleCompleted,
);

export const getTip = (state: AppState): Tip | null => state.receipt?.tip;
export const getTipOptions = (state: AppState): TipOption[] => state.receipt?.tip?.tipOptions || [];

export const getTippableAmount = (state: AppState): number | null => {
  const tippableAmount = state.receipt?.tip?.tippableAmount;
  return tippableAmount === 0 ? 0 : tippableAmount || state.receipt?.tip?.paymentTotal || null;
};

export const isTippingAvailable = createSelector([getTip], tip => {
  if (tip?.tipOptions) {
    return tip.tipOptions.length > 0;
  }
  return false;
});

export const getSaleId = (state: AppState) => state.receipt?.sale?.saleID;
const getCustomer = (state: AppState) => state.receipt?.sale?.Customer;

export const getCustomerInfo = createSelector(
  [getCustomer],
  customer =>
    customer && {
      firstName: customer?.firstName,
      lastName: customer?.lastName,
      customerId: customer?.customerID,
    },
);

export const getPrimaryEmail = createSelector([getCustomer], customer => {
  if (!customer) return undefined;

  const emails = customer.Contact?.Emails;
  if (emails) {
    const { ContactEmail } = emails;
    if (Array.isArray(ContactEmail)) {
      return ContactEmail.filter(email => email.useType === 'Primary')?.[0]?.address;
    }

    return ContactEmail.useType === 'Primary' ? ContactEmail.address : undefined;
  }
  return undefined;
});

export const getMobilePhone = createSelector([getCustomer], customer => {
  if (!customer) return undefined;

  const phones = customer.Contact?.Phones;
  if (phones) {
    const { ContactPhone } = phones;
    if (Array.isArray(ContactPhone)) {
      return ContactPhone.filter(phone => phone.useType === 'Mobile').map(
        phone => phone.number,
      )?.[0];
    }
    return ContactPhone.useType === 'Mobile' ? ContactPhone.number : undefined;
  }
  return undefined;
});
